<template>
  <div class="collapsible-search">
    <!-- 折叠/展开触发器 -->
    <div class="search-header" @click="toggleExpand">
      <div class="header-left">
        <van-icon :name="isExpanded ? 'arrow-down' : 'arrow-right'" size="16" />
        <span class="header-title">{{ title }}</span>
      </div>
      <div v-if="!isExpanded && showSummary" class="header-summary">
        <span class="summary-text">{{ summaryText }}</span>
      </div>
    </div>

    <!-- 搜索表单区域（可折叠） -->
    <transition name="slide-fade">
      <div v-show="isExpanded" class="search-form-container">
        <div class="search-form">
          <!-- 动态搜索内容插槽 -->
          <slot
            name="search-content"
            :form-data="formData"
            :update-form="updateForm"
            :handle-Search="handleSearch"
          />

          <!-- 按钮区域 -->
          <div class="search-actions">
            <van-button
              v-if="showResetBtn"
              class="action-btn reset-btn"
              :size="buttonSize"
              :loading="resetLoading"
              @click="handleReset"
              round
            >
              <van-icon name="replay" />
              <span>{{ resetText }}</span>
            </van-button>

            <van-button
              class="action-btn search-btn"
              type="primary"
              :size="buttonSize"
              :loading="searchLoading"
              @click="handleSearch"
              round
            >
              <van-icon name="search" />
              <span>{{ searchText }}</span>
            </van-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "CollapsibleSearch",
  props: {
    // 标题文字
    title: {
      type: String,
      default: "筛选搜索",
    },
    // 搜索按钮文字
    searchText: {
      type: String,
      default: "搜索",
    },
    // 重置按钮文字
    resetText: {
      type: String,
      default: "重置",
    },
    // 是否显示重置按钮
    showResetBtn: {
      type: Boolean,
      default: true,
    },
    // 按钮尺寸
    buttonSize: {
      type: String,
      default: "small",
      validator: (val) => ["large", "normal", "small", "mini"].includes(val),
    },
    // 初始是否展开
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
    // 是否在折叠时显示搜索条件摘要
    showSummary: {
      type: Boolean,
      default: true,
    },
    // 外部控制搜索加载状态
    searchLoading: {
      type: Boolean,
      default: false,
    },
    // 外部控制重置加载状态
    resetLoading: {
      type: Boolean,
      default: false,
    },
    // 初始表单数据（仅用于初始化，后续由外部控制）
    initialFormData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isExpanded: this.defaultExpanded,
      formData: this.getInitialFormData(),
    };
  },
  computed: {
    // 摘要文本（根据表单数据生成，可通过插槽覆盖展示逻辑）
    summaryText() {
      // 如果有自定义摘要生成函数，使用之
      if (this.$scopedSlots.summary) {
        return "";
      }

      const activeFilters = [];
      for (const [key, value] of Object.entries(this.formData)) {
        if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== false
        ) {
          if (Array.isArray(value) && value.length === 0) continue;
          if (
            typeof value === "object" &&
            value !== null &&
            !Object.keys(value).length
          )
            continue;
          activeFilters.push(this.getFieldLabel(key, value));
        }
      }

      if (activeFilters.length === 0) {
        return "全部条件";
      }
      if (activeFilters.length <= 2) {
        return activeFilters.join(" · ");
      }
      return `${activeFilters.slice(0, 2).join(" · ")} 等${
        activeFilters.length
      }项`;
    },
  },
  watch: {
    initialFormData: {
      deep: true,
      handler(newVal) {
        this.formData = this.getInitialFormData();
      },
    },
  },
  methods: {
    // 获取初始表单数据
    getInitialFormData() {
      return JSON.parse(JSON.stringify(this.initialFormData));
    },

    // 更新表单数据（供插槽调用）
    updateForm(key, value) {
      this.$set(this.formData, key, value);
      // 可选：实时搜索（根据需求可开启）
      // if (this.autoSearch) {
      //   this.handleSearch();
      // }
    },

    // 批量更新表单数据
    updateFormData(data) {
      Object.keys(data).forEach((key) => {
        this.$set(this.formData, key, data[key]);
      });
    },

    // 获取字段的显示标签（用于摘要）
    getFieldLabel(key, value) {
      // 如果父组件提供了标签映射，使用之
      if (this.$attrs.fieldLabels && this.$attrs.fieldLabels[key]) {
        const label = this.$attrs.fieldLabels[key];
        if (Array.isArray(value)) {
          return `${label}: ${value.join(",")}`;
        }
        return `${label}: ${value}`;
      }

      // 默认返回键值对
      if (Array.isArray(value)) {
        return `${key}: ${value.join(",")}`;
      }
      return `${key}: ${value}`;
    },

    // 重置表单
    handleReset() {
      // 重置为初始值
      this.formData = this.getInitialFormData();
      this.$emit("reset", { ...this.formData });
    },

    // 搜索
    handleSearch() {
      // 深拷贝一份数据
      const searchParams = JSON.parse(JSON.stringify(this.formData));
      this.$emit("search", searchParams);
    },

    // 切换展开/折叠
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      this.$emit("toggle", this.isExpanded);
    },

    // 手动展开
    expand() {
      if (!this.isExpanded) {
        this.isExpanded = true;
        this.$emit("toggle", true);
      }
    },

    // 手动折叠
    collapse() {
      if (this.isExpanded) {
        this.isExpanded = false;
        this.$emit("toggle", false);
      }
    },

    // 获取当前表单数据
    getFormData() {
      return { ...this.formData };
    },

    // 设置表单数据
    setFormData(data) {
      this.updateFormData(data);
    },

    // 清空表单数据（重置为空对象）
    clearFormData() {
      const emptyData = {};
      Object.keys(this.formData).forEach((key) => {
        emptyData[key] = "";
      });
      this.updateFormData(emptyData);
    },
  },
};
</script>

<style scoped>
.collapsible-search {
  background-color: #fff;
  border-bottom: 1px solid #f5f6f8;
  margin: 10px 12px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

/* 头部样式 */
.search-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: #fff;
  cursor: pointer;
  transition: background-color 0.2s;
  user-select: none;
}

.search-header:active {
  background-color: #f7f8fa;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.header-title {
  font-size: 14px;
  font-weight: 500;
  color: #323233;
}

.header-summary {
  flex: 1;
  text-align: right;
  margin-left: 10px;
  overflow: hidden;
}

.summary-text {
  font-size: 12px;
  color: #969799;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 搜索表单区域 */
.search-form-container {
  border-top: 1px solid #f5f6f8;
  background-color: #f7f8fa;
}

.search-form {
  padding: 12px;
}

/* 动画效果 */
.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.15s ease-in;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* 按钮区域 */
.search-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 70px;
  height: 32px;
}

.reset-btn {
  background-color: #f2f3f5;
  border-color: #ebedf0;
  color: #646566;
}

.reset-btn:active {
  background-color: #e5e6e8;
}

.search-btn {
  background: #1989fa;
  border: none;
}

.search-btn:active {
  opacity: 0.85;
}
</style>